import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";

const PER_PAGE = 12;

const estadoBadge = {
  pendiente: "tn-badge tn-badge--yellow",
  resuelta:  "tn-badge tn-badge--green",
  rechazada: "tn-badge tn-badge--red",
};

export default function IncidenciasAdmin() {
  const { token, loading } = useContext(AuthContext);
  const [incidencias, setIncidencias] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterEstado, setFilterEstado] = useState("pendiente");
  const [generalError, setGeneralError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });
  const [loadingId, setLoadingId] = useState(null);
  const pendingRef = useRef(false);

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 4000);
  };

  const formatFechaHora = (str) => {
    if (!str) return "";
    const dt = new Date(str.replace(" ", "T"));
    return isNaN(dt) ? str : dt.toLocaleString("es-ES");
  };

  const loadIncidencias = async (currentPage = page, estado = filterEstado) => {
    if (!token) return;
    try {
      const params = { page: currentPage, per_page: PER_PAGE };
      if (estado) params.estado = estado;
      const res = await api.get("/incidencias", { params });
      setIncidencias(res.data.data);
      setTotal(res.data.total);
    } catch {
      setGeneralError("Error al cargar las incidencias.");
    }
  };

  useEffect(() => { loadIncidencias(page, filterEstado); }, [token, page, filterEstado]);

  const handleFiltroEstado = (e) => { setFilterEstado(e.target.value); setPage(1); };

  const handleCambiarEstado = async (id, nuevoEstado) => {
    if (!token || pendingRef.current) return;
    pendingRef.current = true;
    setLoadingId(id);
    try {
      await api.put(`/incidencias/${id}`, { estado: nuevoEstado });
      showToast(`Estado cambiado a "${nuevoEstado}"`);
      loadIncidencias(page, filterEstado);
    } catch (err) {
      setGeneralError(err.response?.data?.error || "Error al cambiar el estado.");
    } finally {
      pendingRef.current = false;
      setLoadingId(null);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--clr-muted)", fontSize: 14 }}>
      Cargando…
    </div>
  );

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div>
      {toast.show && <div className="tn-toast tn-toast--success">{toast.message}</div>}

      <h1 className="tn-page-title">Incidencias</h1>
      <p className="tn-page-sub">Gestiona las incidencias reportadas por los empleados</p>

      {generalError && (
        <div className="tn-alert tn-alert--danger" style={{ marginBottom: 16 }}>{generalError}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <label className="tn-label" style={{ margin: 0, whiteSpace: "nowrap" }}>Filtrar por estado</label>
        <select className="tn-input tn-select" style={{ width: "auto" }} value={filterEstado} onChange={handleFiltroEstado}>
          <option value="">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="resuelta">Resuelta</option>
          <option value="rechazada">Rechazada</option>
        </select>
        <span style={{ fontSize: 13, color: "var(--clr-muted)" }}>{total} resultado{total !== 1 ? "s" : ""}</span>
      </div>

      {incidencias.length > 0 ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14, marginBottom: 20 }}>
            {incidencias.map((i) => (
              <div key={i.id} className="tn-card tn-card--hover" style={{ padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: "var(--clr-muted)" }}>{i.tipo}</span>
                  <span className={estadoBadge[i.estado] || "tn-badge tn-badge--slate"}>{i.estado}</span>
                </div>
                <p style={{ fontSize: 14, fontWeight: 600, color: "var(--clr-text)", margin: "0 0 2px" }}>{i.usuario?.nombre || "—"}</p>
                <p style={{ fontSize: 12.5, color: "var(--clr-muted-lt)", margin: "0 0 8px" }}>{formatFechaHora(i.fecha_hora)}</p>
                <p style={{ fontSize: 13, color: "var(--clr-muted)", margin: "0 0 12px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>{i.comentario}</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {i.estado !== "pendiente" && (
                    <button className="tn-btn tn-btn--ghost" style={{ fontSize: 12, padding: "4px 10px" }} disabled={loadingId !== null} onClick={() => handleCambiarEstado(i.id, "pendiente")}>Pendiente</button>
                  )}
                  {i.estado !== "resuelta" && (
                    <button className="tn-btn tn-btn--success" style={{ fontSize: 12, padding: "4px 10px" }} disabled={loadingId !== null} onClick={() => handleCambiarEstado(i.id, "resuelta")}>Resuelta</button>
                  )}
                  {i.estado !== "rechazada" && (
                    <button className="tn-btn tn-btn--danger" style={{ fontSize: 12, padding: "4px 10px" }} disabled={loadingId !== null} onClick={() => handleCambiarEstado(i.id, "rechazada")}>Rechazada</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="tn-pagination">
              <button className="tn-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} className={`tn-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
              <button className="tn-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
        </>
      ) : (
        <div className="tn-card" style={{ padding: "60px 20px", textAlign: "center", color: "var(--clr-muted)", fontSize: 14 }}>
          No hay incidencias para mostrar
        </div>
      )}
    </div>
  );
}
